/**
 * CR-5454: keep the dashboard's filters and open tab across a page refresh.
 *
 * The acceptance criteria ask for filter changes to "persist after refresh".
 * Saved presets only hold a handful of fields and need the user to save one,
 * so the live state is kept in sessionStorage instead: it survives a reload of
 * the browser tab and is gone when the tab is closed.
 *
 * - Filters are keyed by user AND view scope (level, company, site, group): a
 *   reload after switching site must not bring back another site's filters.
 * - The tab is keyed by user only; a tab the scope cannot show falls back to
 *   the first visible one (Dashboard.tsx already does that).
 * - Every read and write is guarded: storage can be unavailable (private mode,
 *   blocked site data) or hold a malformed value, and the dashboard must then
 *   simply start from its defaults.
 *
 * Framework-free and unit-tested (see __tests__/dashboardSession.test.ts).
 */
import type { DashboardFilterState } from './types';

const PREFIX = 'helix:dashboard:v1';

/** The view scope the filters were chosen in. */
export interface DashboardScope {
  viewLevel?: string | null
  selectedCompanyId?: number | string | null
  selectedSiteId?: number | string | null
  selectedGroupId?: number | string | null
}

type UserId = number | string | null | undefined;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function filtersKey(userId: UserId, scope: DashboardScope): string {
  const parts = [scope.viewLevel, scope.selectedCompanyId, scope.selectedSiteId, scope.selectedGroupId]
    .map((v) => (v === null || v === undefined ? '' : String(v)));
  return `${PREFIX}:filters:${userId}:${parts.join(':')}`;
}

function tabKey(userId: UserId): string {
  return `${PREFIX}:tab:${userId}`;
}

/**
 * The filters saved for this user and scope in this browser tab.
 *
 * @param userId - the signed-in user; nothing is read without one
 * @param scope - the current view scope
 * @returns the saved filters, or null when there are none or they are unusable
 */
export function readSessionFilters(userId: UserId, scope: DashboardScope): DashboardFilterState | null {
  const s = storage();
  if (!s || userId === null || userId === undefined) return null;
  try {
    const raw = s.getItem(filtersKey(userId, scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const f = parsed as Partial<DashboardFilterState>;
    if (typeof f.dateFrom !== 'string' || typeof f.dateTo !== 'string') return null;
    return parsed as DashboardFilterState;
  } catch {
    return null;
  }
}

/**
 * Save the filters for this user and scope.
 *
 * @param userId - the signed-in user; nothing is saved without one
 * @param scope - the current view scope
 * @param filters - the dashboard's current filter state
 */
export function writeSessionFilters(userId: UserId, scope: DashboardScope, filters: DashboardFilterState): void {
  const s = storage();
  if (!s || userId === null || userId === undefined) return;
  try {
    s.setItem(filtersKey(userId, scope), JSON.stringify(filters));
  } catch {
    // Quota or blocked storage: the dashboard still works, it just won't remember.
  }
}

/**
 * The dashboard tab this user last had open in this browser tab.
 *
 * @param userId - the signed-in user
 * @returns the tab id, or null
 */
export function readSessionTab(userId: UserId): string | null {
  const s = storage();
  if (!s || userId === null || userId === undefined) return null;
  try {
    return s.getItem(tabKey(userId)) || null;
  } catch {
    return null;
  }
}

/**
 * Remember the open dashboard tab.
 *
 * @param userId - the signed-in user
 * @param tab - the tab id
 */
export function writeSessionTab(userId: UserId, tab: string): void {
  const s = storage();
  if (!s || userId === null || userId === undefined) return;
  try {
    s.setItem(tabKey(userId), tab);
  } catch {
    // See writeSessionFilters.
  }
}

/**
 * CR-8611: the Cost tab's Future / Historical sub-tab this user last had open.
 *
 * @param userId - the signed-in user
 * @returns 'future' / 'historical', or null
 */
export function readSessionCostView(userId: UserId): string | null {
  const s = storage();
  if (!s || userId === null || userId === undefined) return null;
  try {
    return s.getItem(`${PREFIX}:costview:${userId}`) || null;
  } catch {
    return null;
  }
}

/**
 * Remember the Cost sub-tab.
 *
 * @param userId - the signed-in user
 * @param view - 'future' / 'historical'
 */
export function writeSessionCostView(userId: UserId, view: string): void {
  const s = storage();
  if (!s || userId === null || userId === undefined) return;
  try {
    s.setItem(`${PREFIX}:costview:${userId}`, view);
  } catch {
    // See writeSessionFilters.
  }
}
